import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { useVeri, tarihSaat } from "@/lib/veri";
import { Band, Bos, Buton, Etiket, Hata, Kutu, Modal, Sayfa, girdi } from "@/components/parcalar";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";

interface Is {
  id: string; tamirId: string | null; atanabilir: boolean; yol: string | null; riserRn: string; hat: string; poz: string | null; sn: string | null; is: string; rotaKod: string;
  adim: number; adimAd: string; adimSayisi: number; bitti: boolean; malzemeBekliyor: boolean;
  islem: string; islemEtiket: string; engel: string; engelliMi: boolean;
  atanan: { id: string; ad: string; rol: string; rolEtiket: string } | null;
  atamaKim: string | null; atamaTs: string | null;
  malzemeEksik: boolean; yetkim: boolean; hedef: "komponent" | "fitup" | "weldmap";
}
interface Veri {
  ben: { id: string; ad: string; rol: string; rolEtiket: string };
  bana: Is[]; havuz: Is[]; digerAtanmis: Is[];
  sayilar: { bana: number; havuz: number; engelli: number };
}
interface Kisi { id: string; ad: string; rol: string; rolEtiket: string }

/**
 * İşlerim: kişiye atanmış işler + rolün havuzundaki atanmamış işler.
 * İş = bir komponentin sıradaki rota adımı; hangi rolün yetkisine düştüğü yetki matrisinden gelir.
 */
export function Islerim() {
  const { veri, hata, yenile } = useVeri<Veri>("/islerim");
  const { veri: kisiler } = useVeri<Kisi[]>("/islerim/kisiler");
  const [atama, setAtama] = useState<{ is: Is; kisiId: string } | null>(null);
  const [islem, setIslem] = useState<string | null>(null);
  const nav = useNavigate();

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <Bos>{cevir("Loading...")}</Bos>;

  const ac = (x: Is) =>
    x.tamirId
      ? nav({ to: x.hedef === "fitup" ? "/fitup/$id" : x.hedef === "weldmap" ? "/weldmap/$id" : "/komponent/$id", params: { id: x.tamirId } })
      : nav({ to: x.yol as "/teslim" });
  const ata = async (isId: string, kisiId: string | null) => {
    setIslem(null);
    try {
      await api.post(`/islerim/${isId}/ata`, { kullaniciId: kisiId });
      await yenile();
      setAtama(null);
    } catch (e) {
      setIslem(e instanceof Error ? e.message : cevir("Assignment failed"));
    }
  };

  const Satir = ({ x, mod }: { x: Is; mod: "bana" | "havuz" | "diger" }) => (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 hover:bg-surface-2">
      <button onClick={() => void ac(x)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <b className="font-mono">{x.riserRn}</b>
            <span className="text-ink-2">{x.hat}{x.poz ? ` · ${x.poz}` : ""}</span>
            {x.rotaKod && <Etiket>{x.rotaKod}</Etiket>}
            {x.malzemeBekliyor ? <Etiket tone="bad">{cevir("waiting for material")}</Etiket> : <Etiket tone={x.engelliMi ? "warn" : "info"}>{cevir(x.adimAd)}</Etiket>}
            {x.adimSayisi > 0 && !x.malzemeBekliyor && <span className="text-xs text-ink-3 tnum">{x.adim + 1}/{x.adimSayisi}</span>}
          </span>
          <span className="mt-0.5 block truncate text-sm text-ink-2">{cevir(x.is)}</span>
          <span className="mt-0.5 block text-xs text-ink-3">
            {cevir("Permission:")} {x.islemEtiket.split(" · ").map((r) => cevir(r)).join(" · ")}
            {mod === "diger" && x.atanan ? cevir` · assigned: ${x.atanan.ad}` : ""}
            {mod === "bana" && x.atamaKim ? cevir` · assigned by: ${x.atamaKim} (${tarihSaat(x.atamaTs)})` : ""}
          </span>
          {x.engel && <span className="mt-1 block text-xs text-warn">{cevir(x.engel)}</span>}
        </span>
        <ArrowRight size={15} className="shrink-0 text-ink-3" />
      </button>
      <span className="flex shrink-0 gap-1.5">
        {mod !== "bana" && x.atanabilir && <Buton kucuk islem="planla" onClick={() => setAtama({ is: x, kisiId: x.atanan?.id ?? veri.ben.id })}><UserPlus size={13} /> {x.atanan ? cevir("Change") : cevir("Assign")}</Buton>}
        {mod === "bana" && <Buton kucuk islem="planla" onClick={() => void ata(x.id, null)}>{cevir("Leave")}</Buton>}
      </span>
    </li>
  );

  return (
    <Sayfa
      ust={cevir("General")}
      baslik={cevir("My work")}
      alt={<>{cevir("Tasks assigned to you on top, tasks in your role's pool on bottom. Source: next route step of repair Items and queues for Receiving, Disassembly, field Document, MRB, verification and Assembly report. Which task falls to which role comes from permission matrix. Assignment is currently on repair Items (Foreman / Supervisor / PM).")}</>}
      aksiyon={<span className="text-sm text-ink-2">{veri.ben.ad} · {cevir(veri.ben.rolEtiket)}</span>}
    >
      <Hata mesaj={islem} />

      <div className="grid gap-2.5 sm:grid-cols-3">
        {[
          { ad: cevir("Assigned to me"), deger: veri.sayilar.bana, ton: "text-ink" },
          { ad: cevir("In role pool"), deger: veri.sayilar.havuz, ton: "text-info" },
          { ad: cevir("Blocked (solve condition first)"), deger: veri.sayilar.engelli, ton: "text-warn" },
        ].map((k) => (
          <div key={k.ad} className="rounded-lg border border-line bg-surface p-3 shadow-[var(--shadow)]">
            <div className="text-xs font-medium text-ink-2">{cevir(k.ad)}</div>
            <div className={cn("mt-1 text-3xl font-semibold leading-none tnum", k.ton)}>{k.deger}</div>
          </div>
        ))}
      </div>

      <Kutu baslik={cevir("Tasks assigned to me")} aksiyon={cevir`${veri.bana.length} tasks`} padded={false}>
        <ul className="divide-y divide-line">
          {veri.bana.map((x) => <Satir key={x.id} x={x} mod="bana" />)}
          {!veri.bana.length && <li><Bos>{cevir("No open tasks assigned to you. You can take ownership from the pool below.")}</Bos></li>}
        </ul>
      </Kutu>

      <Kutu baslik={cevir("My role's pool · unassigned tasks")} aksiyon={cevir`${veri.havuz.length} tasks`} padded={false}>
        <ul className="divide-y divide-line">
          {veri.havuz.map((x) => <Satir key={x.id} x={x} mod="havuz" />)}
          {!veri.havuz.length && <li><Bos>{cevir("No open tasks falling under your role's permission.")}</Bos></li>}
        </ul>
      </Kutu>

      {veri.digerAtanmis.length > 0 && (
        <Kutu baslik={cevir("Assigned to others")} aksiyon={cevir`${veri.digerAtanmis.length} tasks`} padded={false}>
          <ul className="divide-y divide-line">
            {veri.digerAtanmis.map((x) => <Satir key={x.id} x={x} mod="diger" />)}
          </ul>
        </Kutu>
      )}

      <Band tip="bilgi">{cevir("Assignment ties task to person but does not change permission: permission to close step is still role's permission. Unassigned task appears in pool of everyone authorized.")}</Band>

      {atama && (
        <Modal baslik={cevir`Assign task — ${atama.is.riserRn} ${atama.is.hat}`} onKapat={() => setAtama(null)}>
          <p className="mb-2 text-sm text-ink-2">{atama.is.adimAd} {cevir("· permission:")} {atama.is.islemEtiket}</p>
          <select className={girdi} value={atama.kisiId} onChange={(e) => setAtama({ ...atama, kisiId: e.target.value })}>
            {(kisiler ?? []).map((k) => <option key={k.id} value={k.id}>{k.ad} — {cevir(k.rolEtiket)}</option>)}
          </select>
          <div className="mt-3 flex flex-wrap gap-2">
            <Buton tur="birincil" islem="planla" onClick={() => void ata(atama.is.id, atama.kisiId)}>{cevir("Assign")}</Buton>
            {atama.is.atanan && <Buton islem="planla" onClick={() => void ata(atama.is.id, null)}>{cevir("Remove assignment")}</Buton>}
            <Buton onClick={() => setAtama(null)}>{cevir("Cancel")}</Buton>
          </div>
        </Modal>
      )}
    </Sayfa>
  );
}
